/**
 * Send a patient somewhere.
 *
 * The desk, the clinics manager and the doctor all route with this one form,
 * so the routing reads the same everywhere: pick the department from eight
 * big tiles, say how urgent it is, list what is needed, and — for a surgery —
 * name the surgeon. Where it lands is the server's routing table
 * (lib/serviceRouting.js), stated under the tiles so the person sending knows
 * which desk will see it.
 *
 * Prices are optional here. When the sender knows them, "احسب نصيب المريض"
 * shows what the patient's cover pays before anything is sent; Accounts prices
 * whatever is left blank.
 */

import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Field, Icon, Input, Select, Textarea } from '../../components/common';
import { Tile3D } from '../../components/common/Tile3D';
import { clinicsAdminApi, requestsApi } from '../../api/endpoints';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { DEPARTMENT_META, DEPARTMENTS, PRIORITY_META, formatMoney } from './labels';
import { usePermissions } from '../../context/PermissionContext';
import { DEPARTMENT_CONTROL } from '../../domain/serviceControl';

/** Where each department's request lands — the routing table, in words. */
const LANDS_AT = Object.freeze({
  checkup: 'يصل إلى استقبال العيادات',
  lab: 'يصل إلى المعمل',
  radiology: 'يصل إلى قسم الأشعة',
  pharmacy: 'يصل إلى الصيدلية',
  medication: 'يصل إلى مدير الغرف ثم الصيدلية',
  icu: 'يصل إلى مدير الغرف (العناية المركزة)',
  ward: 'يصل إلى مدير الغرف (الأقسام الداخلية)',
  surgery: 'يصل إلى مدير الغرف وإلى الجراح — ويلزم موافقة الاثنين',
});

/** What the item rows are called, per department. */
const ITEM_HINT = Object.freeze({
  checkup: 'مثال: كشف باطنة',
  lab: 'مثال: صورة دم كاملة CBC',
  radiology: 'مثال: أشعة مقطعية على الصدر',
  pharmacy: 'مثال: أموكسيسيلين 500 مجم',
  medication: 'مثال: محلول ملح 500 مل',
  icu: 'مثال: إقامة عناية مركزة — ليلة',
  ward: 'مثال: إقامة قسم باطنة — ليلة',
  surgery: 'مثال: استئصال الزائدة الدودية',
});

const blankItem = () => ({ name: '', quantity: 1, unitPrice: '', instructions: '' });

export function RequestComposer({
  patient,
  bookingId = null,
  departments = DEPARTMENTS,
  defaultDepartment = null,
  onCreated,
}) {
  const { user } = useAuth();
  const { success, error: toastError } = useNotifications();
  // «طلب خدمة» shows the departments the package carries, and no other.
  const { serviceCreates } = usePermissions();
  const offered = departments.filter((key) => serviceCreates(DEPARTMENT_CONTROL[key] || ''));

  const [department, setDepartment] = useState(defaultDepartment);
  const [priority, setPriority] = useState('normal');
  const [items, setItems] = useState([blankItem()]);
  const [notes, setNotes] = useState('');
  const [doctorId, setDoctorId] = useState('');
  const [doctors, setDoctors] = useState(null);
  const [doctorsError, setDoctorsError] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [sending, setSending] = useState(false);

  const isDoctor = user?.role === 'doctor';
  const needsDoctor = department === 'surgery' || department === 'checkup';

  // The hospital's doctors, for the surgeon (or the checkup's doctor). A doctor
  // sending their own request is the surgeon unless they name somebody else.
  useEffect(() => {
    if (!needsDoctor || doctors) return;
    let cancelled = false;
    clinicsAdminApi
      .list()
      .then((result) => {
        if (!cancelled) setDoctors(Array.isArray(result?.doctors) ? result.doctors : []);
      })
      .catch((err) => {
        if (!cancelled) {
          setDoctors([]);
          setDoctorsError(err.message);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [needsDoctor, doctors]);

  // Any change invalidates the last quote.
  useEffect(() => setQuote(null), [department, priority, items]);

  const cleanItems = useMemo(
    () =>
      items
        .filter((item) => item.name.trim())
        .map((item) => ({
          name: item.name.trim(),
          quantity: Math.max(1, parseInt(item.quantity, 10) || 1),
          unitPrice: item.unitPrice === '' ? null : Number(item.unitPrice),
          instructions: item.instructions.trim() || null,
        })),
    [items],
  );

  const allPriced = cleanItems.length > 0 && cleanItems.every((item) => item.unitPrice !== null);

  const setItem = (index, patch) => setItems((list) => list.map((item, i) => (i === index ? { ...item, ...patch } : item)));

  const payload = () => ({
    hospitalPatientId: patient.id,
    department,
    priority,
    items: cleanItems,
    notes: notes.trim() || null,
    doctorId: doctorId ? Number(doctorId) : null,
    bookingId,
  });

  const runQuote = async () => {
    setQuoting(true);
    try {
      setQuote(await requestsApi.quote(payload()));
    } catch (err) {
      toastError(err.message);
    } finally {
      setQuoting(false);
    }
  };

  const send = async () => {
    if (!department) return toastError('اختر القسم أولاً');
    if (cleanItems.length === 0) return toastError('اكتب المطلوب — بند واحد على الأقل');
    if (department === 'surgery' && !doctorId && !isDoctor) return toastError('حدد الجراح المسؤول عن العملية');
    setSending(true);
    try {
      const created = await requestsApi.create(payload());
      success(`تم إرسال الطلب — ${LANDS_AT[department]}`);
      setItems([blankItem()]);
      setNotes('');
      setQuote(null);
      setPriority('normal');
      onCreated?.(created);
    } catch (err) {
      toastError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="mb-2 text-xs font-bold text-slate-400">١ — إلى أين؟</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {offered.map((key, i) => {
            const meta = DEPARTMENT_META[key];
            return (
              <Tile3D
                key={key}
                icon={meta.icon}
                label={meta.label}
                tone={department === key ? meta.tone : 'slate'}
                pulse={department === key}
                enter
                delay={i}
                onClick={() => setDepartment(key)}
                aria-pressed={department === key}
              />
            );
          })}
        </div>
        {department && (
          <p className="mt-2 flex items-center gap-1.5 text-xs text-cyan-300">
            <Icon name="navigation" size={13} /> {LANDS_AT[department]}
          </p>
        )}
      </div>

      {department && (
        <>
          <div>
            <p className="mb-2 text-xs font-bold text-slate-400">٢ — ما مدى الاستعجال؟</p>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="الأولوية">
              {Object.entries(PRIORITY_META).map(([key, meta]) => (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={priority === key}
                  onClick={() => setPriority(key)}
                  className={`flex min-h-[44px] items-center gap-2 rounded-xl border px-4 text-sm font-bold transition-all ${
                    priority === key ? `${meta.className} scale-[1.03] shadow-lg` : 'border-white/10 bg-white/[0.03] text-slate-400 hover:text-white'
                  }`}
                >
                  <Icon name={meta.icon} size={16} /> {meta.label}
                </button>
              ))}
            </div>
            {priority === 'emergency' && (
              <p className="mt-2 text-xs text-red-300">
                الحالة الطارئة يستطيع القسم قبولها فوراً، والموافقة المالية تلحق بها.
              </p>
            )}
          </div>

          <div>
            <p className="mb-2 text-xs font-bold text-slate-400">٣ — المطلوب</p>
            <div className="space-y-2">
              {items.map((item, index) => (
                <div
                  key={index}
                  className="grid grid-cols-[1fr_auto] gap-2 rounded-xl border border-white/5 bg-white/[0.02] p-2 sm:grid-cols-[2fr_0.6fr_0.9fr_1.4fr_auto]"
                >
                  <Input
                    value={item.name}
                    onChange={(e) => setItem(index, { name: e.target.value })}
                    placeholder={ITEM_HINT[department]}
                    aria-label="اسم البند"
                    className="col-span-2 sm:col-span-1"
                  />
                  <Input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) => setItem(index, { quantity: e.target.value })}
                    aria-label="الكمية"
                  />
                  <Input
                    type="number"
                    min="0"
                    value={item.unitPrice}
                    onChange={(e) => setItem(index, { unitPrice: e.target.value })}
                    placeholder="السعر (اختياري)"
                    aria-label="سعر الوحدة"
                  />
                  <Input
                    value={item.instructions}
                    onChange={(e) => setItem(index, { instructions: e.target.value })}
                    placeholder="تعليمات"
                    aria-label="تعليمات"
                    className="col-span-2 sm:col-span-1"
                  />
                  <Button
                    variant="subtle"
                    icon="trash-2"
                    aria-label="حذف البند"
                    disabled={items.length === 1}
                    onClick={() => setItems((list) => list.filter((_, i) => i !== index))}
                  />
                </div>
              ))}
              <Button variant="subtle" icon="plus" onClick={() => setItems((list) => [...list, blankItem()])}>
                بند آخر
              </Button>
            </div>
          </div>

          {needsDoctor && (
            <Field
              label={department === 'surgery' ? 'الجراح المسؤول' : 'الطبيب (اختياري)'}
              required={department === 'surgery' && !isDoctor}
              hint={
                department === 'surgery'
                  ? isDoctor
                    ? 'اتركه فارغاً لتكون أنت الجراح'
                    : 'الجراح يوافق ويحدد موعد العملية من لوحته'
                  : undefined
              }
              error={doctorsError ? `تعذر تحميل قائمة الأطباء: ${doctorsError}` : undefined}
            >
              <Select value={doctorId} onChange={(e) => setDoctorId(e.target.value)}>
                <option value="">{isDoctor ? 'أنا' : '— اختر —'}</option>
                {(doctors || []).map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                    {d.profile?.title ? ` — ${d.profile.title}` : ''}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          <Field label="ملاحظات للقسم">
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="أي تفاصيل يحتاجها القسم" />
          </Field>

          {quote && (
            <div className="rounded-xl border border-purple-500/25 bg-purple-500/[0.07] p-3 text-sm" aria-live="polite">
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="border-white/10 bg-white/5 text-slate-200">الإجمالي {formatMoney(quote.total)}</Badge>
                <Badge className="border-purple-500/30 bg-purple-500/10 text-purple-200">يغطيه التأمين {formatMoney(quote.covered)}</Badge>
                <Badge className="border-amber-500/40 bg-amber-500/15 text-amber-100">
                  <Icon name="wallet" size={12} /> على المريض {formatMoney(quote.patientShare)}
                </Badge>
              </div>
              {!quote.insured && (
                <p className="mt-2 text-xs text-slate-400">
                  {quote.reason === 'expired'
                    ? 'انتهت صلاحية تأمين هذا المريض — يُحاسب نقداً.'
                    : quote.reason === 'no-coverage'
                      ? 'لم تُسجَّل تغطية لهذا المريض بعد.'
                      : 'مريض نقدي.'}
                </p>
              )}
              <ul className="mt-2 space-y-0.5 text-xs text-slate-300">
                {quote.lines.map((line, i) => (
                  <li key={i} className="flex justify-between gap-3">
                    <span className="truncate">{line.label}</span>
                    <span className="shrink-0 text-slate-400">{line.note}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap justify-end gap-2 border-t border-white/5 pt-4">
            <Button variant="ghost" icon="percent" loading={quoting} disabled={!allPriced} onClick={runQuote}>
              احسب نصيب المريض
            </Button>
            <Button icon="send" loading={sending} onClick={send}>
              إرسال الطلب
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export default RequestComposer;
